import { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../api';
import { Button, Card, EmptyState, Loading, Money, PageHeader, Select, Table } from '../ui';
import type {
  CompetitorComparisonData,
  VendorProposal,
  ProposalCompetitorDetail,
  ProductStrategy,
  ReplacedProductHandling,
  SystemSettings,
} from '../shared/types';
import {
  calculateGiveawayEffectiveCost,
  calculateNormalizedUnitPrice,
} from '../shared/competitor-comparison';
import { calculateGrossMargin } from '../shared/domain';
import { effectiveProductTradeTerms } from '../shared/trade-terms';
import {
  labelCertificateType,
  PRODUCT_STRATEGY_LABELS,
  REPLACED_PRODUCT_HANDLING_LABELS,
  COMPARISON_REPLACEMENT_LABELS,
  DEFAULT_SETTINGS,
  PROACTIVE_REJECTION_REASONS,
} from '../shared/constants';
import {
  formatVendorCategories,
  formatVendorReturnConditions,
  formatVendorWeekdays,
  labelVendorDeliveryCycle,
  labelVendorDeliveryScope,
  labelVendorFreightRule,
  labelVendorInvoiceDelivery,
  labelVendorInvoiceFormat,
  labelVendorPaymentMethod,
  labelVendorPaymentTerm,
  labelVendorReturnFreight,
  labelVendorReturnHandling,
  labelVendorStatus,
} from '../shared/vendor-labels';
import type { Notify } from '../pages';

const LAUNCH_PRODUCT_STRATEGIES: ProductStrategy[] = [
  'REPLACE',
  'COEXIST',
  'TRIAL',
  'LIMITED_STORE',
  'NEW_CATEGORY',
];

interface MeetingSnapshotData {
  proposal?: Record<string, any>;
  competitors?: ProposalCompetitorDetail[];
  comparison?: CompetitorComparisonData['comparison'];
  supplier_decision_snapshot?: Record<string, any> | null;
}

function hasValue(value: unknown): boolean {
  return value !== null && value !== undefined && String(value).trim() !== '';
}

function displayValue(value: unknown, empty = '—'): string {
  if (!hasValue(value)) return empty;
  if (Array.isArray(value)) return value.length ? value.join('、') : empty;
  return String(value);
}

function parseList(value: unknown): unknown[] {
  if (Array.isArray(value)) return value;
  if (!hasValue(value)) return [];
  try {
    const parsed = JSON.parse(String(value));
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatIngredientSummary(competitor?: ProposalCompetitorDetail | null): string {
  if (!competitor) return '—';
  const ingredients = parseList(competitor.ingredients);
  if (ingredients.length > 0) {
    return ingredients
      .map((item) => {
        if (typeof item === 'string') return item;
        if (!item || typeof item !== 'object') return '';
        const value = item as Record<string, unknown>;
        const name =
          value.ingredient_name_normalized || value.ingredient_name_raw || value.name || '';
        const amount = hasValue(value.amount)
          ? ` ${value.amount}${displayValue(value.unit, '')}`
          : '';
        return `${name}${amount}`.trim();
      })
      .filter(Boolean)
      .join('、');
  }
  return displayValue(competitor.ingredient_raw_text);
}

function comparisonFlagLabel(value: unknown): string {
  if (value === null || value === undefined || value === '') return '待確認';
  return Number(value) === 1 || value === true ? '是' : '否';
}

function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return '操作失敗，請稍後再試。';
}

function getProposalField(proposal: any, field: string): any {
  if (!proposal) return undefined;
  if (proposal[field] !== undefined) return proposal[field];
  if (proposal.product && proposal.product[field] !== undefined) return proposal.product[field];
  if (field === 'product_name')
    return proposal.product?.name_zh || proposal.name_zh || proposal.product_name;
  if (field === 'proposal_number')
    return proposal.proposal_no || proposal.proposal_number || proposal.id;
  return undefined;
}

export function UpgradedMeetingModePage({ notify }: { notify: Notify }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const proposalIdParam = searchParams.get('proposal_id') || searchParams.get('id') || '';

  const [candidates, setCandidates] = useState<VendorProposal[]>([]);
  const [selectedId, setSelectedId] = useState(proposalIdParam);
  const [loadingList, setLoadingList] = useState(false);

  const [selectedProposal, setSelectedProposal] = useState<VendorProposal | null>(null);
  const [comparisonData, setComparisonData] = useState<CompetitorComparisonData | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // 供應商資訊 Drawer 狀態
  const [showSupplierDrawer, setShowSupplierDrawer] = useState(false);
  const [supplierDetail, setSupplierDetail] = useState<any>(null);
  const [loadingSupplier, setLoadingSupplier] = useState(false);

  // 完整競品比較彈窗 (Modal)
  const [showFullCompareModal, setShowFullCompareModal] = useState(false);

  // 會議決策表單狀態
  const [decision, setDecision] = useState<
    'APPROVED' | 'REVISION_REQUIRED' | 'PAUSED' | 'NOT_ADOPTED'
  >('APPROVED');
  const [notes, setNotes] = useState('');
  const [revisionDueAt, setRevisionDueAt] = useState('');
  const [reconsiderationDate, setReconsiderationDate] = useState('');
  const [revisionItems, setRevisionItems] = useState<string[]>(['PRODUCT_BASIC']);
  const [rejectionReasonCode, setRejectionReasonCode] = useState('');
  const [vendorVisibleReason, setVendorVisibleReason] = useState('');
  const [deciding, setDeciding] = useState(false);

  // 商品策略與舊品去化決策
  const [productStrategy, setProductStrategy] = useState<ProductStrategy>('REPLACE');
  const [replacedProductId, setReplacedProductId] = useState<string>('');
  const [replacedHandling, setReplacedHandling] = useState<ReplacedProductHandling>('SELL_OUT');
  const [replacedHandlingOther, setReplacedHandlingOther] = useState('');
  const [coexistenceReason, setCoexistenceReason] = useState('');
  const [systemSettings, setSystemSettings] = useState<SystemSettings>(DEFAULT_SETTINGS);

  const meetingToday = new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  // 載入待會議審查清單
  useEffect(() => {
    setLoadingList(true);
    api.proactive
      .list({ status: 'WAITING_MEETING' })
      .then((res) => {
        setCandidates(res.proposals);
        if (!selectedId && res.proposals.length > 0) {
          const firstId = res.proposals[0]?.id || '';
          setSelectedId(firstId);
          setSearchParams({ proposal_id: firstId });
        }
      })
      .catch((err) => notify(errorMessage(err), 'error'))
      .finally(() => setLoadingList(false));
  }, []);

  useEffect(() => {
    api.settings
      .get()
      .then((result) => setSystemSettings(result.settings))
      .catch(() => undefined);
  }, []);

  // 載入提案與競品快照
  const loadDetail = async (id: string) => {
    if (!id) return;
    setLoadingDetail(true);
    setSupplierDetail(null);
    setShowSupplierDrawer(false);
    setDecision('APPROVED');
    setNotes('');
    setRevisionDueAt('');
    setReconsiderationDate('');
    setRevisionItems(['PRODUCT_BASIC']);
    setRejectionReasonCode('');
    setVendorVisibleReason('');
    setProductStrategy('REPLACE');
    setReplacedProductId('');
    setReplacedHandling('SELL_OUT');
    setReplacedHandlingOther('');
    setCoexistenceReason('');
    try {
      const [proposalRes, compRes] = await Promise.all([
        api.proactive.detail(id),
        api.competitorComparisons.get(id).catch(() => null),
      ]);
      setSelectedProposal(proposalRes);
      setComparisonData(compRes);

      // 預設被取代商品為主要競品
      const snapshot = compRes?.meeting_snapshot as MeetingSnapshotData | null | undefined;
      const decisionCompetitors = snapshot?.competitors?.length
        ? snapshot.competitors
        : compRes?.competitors || [];
      const primary =
        decisionCompetitors.find((competitor) => competitor.is_primary) ||
        compRes?.primary_competitor;
      if (primary) {
        setReplacedProductId(primary.id);
      } else if (decisionCompetitors.length > 0) {
        setReplacedProductId(decisionCompetitors[0].id);
      }
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setLoadingDetail(false);
    }
  };

  useEffect(() => {
    if (selectedId) {
      void loadDetail(selectedId);
    }
  }, [selectedId]);

  const handleSelect = (id: string) => {
    setSelectedId(id);
    setSearchParams({ proposal_id: id });
  };

  // 打開供應商資訊 Drawer
  const handleOpenSupplierDrawer = async () => {
    setShowSupplierDrawer(true);
    if (!supplierDetail && selectedProposal?.vendor_id) {
      setLoadingSupplier(true);
      try {
        const vendorData = await api.vendors.detail(selectedProposal.vendor_id);
        setSupplierDetail(vendorData);
      } catch (err) {
        notify(errorMessage(err), 'error');
      } finally {
        setLoadingSupplier(false);
      }
    }
  };

  // 執行會議決策提交
  const handleDecide = async () => {
    if (!selectedProposal) return;
    if (decision !== 'APPROVED' && !notes.trim()) {
      notify('請填寫決策說明與原因。', 'error');
      return;
    }
    if (decision === 'REVISION_REQUIRED' && !revisionDueAt) {
      notify('請選擇補件期限。', 'error');
      return;
    }
    if (decision === 'REVISION_REQUIRED' && revisionItems.length === 0) {
      notify('請至少選擇一項補件項目。', 'error');
      return;
    }
    if (decision === 'APPROVED') {
      if (productStrategy === 'REPLACE' && !replacedProductId) {
        notify('決議取代既有商品時，必須指定被取代之惠生商品。', 'error');
        return;
      }
      if (
        productStrategy === 'REPLACE' &&
        replacedHandling === 'OTHER' &&
        !replacedHandlingOther.trim()
      ) {
        notify('選擇其他既有商品處理方式時，請填寫具體說明。', 'error');
        return;
      }
      if (productStrategy === 'COEXIST' && !coexistenceReason.trim()) {
        notify('決議與既有商品並行販售時，請填寫並行原因（如不同價格帶、客群等）。', 'error');
        return;
      }
    }
    if (decision === 'NOT_ADOPTED') {
      if (!rejectionReasonCode) {
        notify('請選擇不採用原因。', 'error');
        return;
      }
      if (!vendorVisibleReason.trim()) {
        notify('請填寫廠商可見的不採用說明。', 'error');
        return;
      }
    }

    setDeciding(true);
    try {
      if (decision === 'REVISION_REQUIRED') {
        await api.proactive.requestRevision(selectedProposal.id, {
          item_codes: revisionItems,
          explanation: notes,
          due_at: revisionDueAt,
        });
        notify('已要求廠商補件！');
      } else {
        await api.proactive.status(selectedProposal.id, {
          status: decision,
          reason: decision === 'APPROVED' ? notes || '會議決議上市。' : notes,
          reconsideration_date: decision === 'PAUSED' ? reconsiderationDate || null : null,
          rejection_reason_code: decision === 'NOT_ADOPTED' ? rejectionReasonCode : undefined,
          internal_rejection_reason: decision === 'NOT_ADOPTED' ? notes : undefined,
          vendor_visible_reason:
            decision === 'NOT_ADOPTED' ? vendorVisibleReason.trim() : undefined,
          meeting_strategy:
            decision === 'APPROVED'
              ? {
                  product_strategy: productStrategy,
                  replaced_product_id: productStrategy === 'REPLACE' ? replacedProductId : null,
                  replaced_product_handling:
                    productStrategy === 'REPLACE' ? replacedHandling : null,
                  replaced_product_handling_other:
                    productStrategy === 'REPLACE' && replacedHandling === 'OTHER'
                      ? replacedHandlingOther.trim()
                      : '',
                  coexistence_reason: productStrategy === 'COEXIST' ? coexistenceReason.trim() : '',
                }
              : undefined,
        });
        notify(
          decision === 'APPROVED'
            ? '會議決議上市！案件已進入待建立正式商品主檔工作台。'
            : '決策已儲存。',
        );
      }

      // 重新整理清單
      const res = await api.proactive.list({ status: 'WAITING_MEETING' });
      setCandidates(res.proposals);
      if (res.proposals.length > 0) {
        const nextId = res.proposals[0]?.id || '';
        setSelectedId(nextId);
        setSearchParams(nextId ? { proposal_id: nextId } : {});
      } else {
        setSelectedId('');
        setSearchParams({});
        setSelectedProposal(null);
        setComparisonData(null);
      }
    } catch (err) {
      notify(errorMessage(err), 'error');
    } finally {
      setDeciding(false);
    }
  };

  const meetingSnapshot = comparisonData?.meeting_snapshot as
    MeetingSnapshotData | null | undefined;
  // 會議決策固定使用「進入會議」當下快照；只有舊資料缺少快照時才退回即時資料。
  const competitors: ProposalCompetitorDetail[] = meetingSnapshot?.competitors?.length
    ? meetingSnapshot.competitors
    : comparisonData?.competitors || [];
  const meetingComparison = meetingSnapshot?.comparison ?? comparisonData?.comparison ?? null;
  const primaryComp: ProposalCompetitorDetail | null =
    competitors.find((c) => c.is_primary) ||
    (!meetingSnapshot?.competitors?.length
      ? (comparisonData?.primary_competitor as ProposalCompetitorDetail | null)
      : null) ||
    null;
  const secondaryComps = competitors.filter((c) => !c.is_primary);
  const comparisonProposal = (meetingSnapshot?.proposal || comparisonData?.proposal) as
    Record<string, any> | undefined;
  const proposalPrices = (comparisonProposal?.prices || {}) as Record<string, unknown>;
  const proposalOfferTerms = (comparisonProposal?.terms || {}) as Record<string, unknown>;
  const readProposalField = (...fields: string[]): any => {
    for (const field of fields) {
      const compared = getProposalField(comparisonProposal, field);
      if (hasValue(compared)) return compared;
      if (hasValue(proposalPrices[field])) return proposalPrices[field];
      if (hasValue(proposalOfferTerms[field])) return proposalOfferTerms[field];
      const current = getProposalField(selectedProposal, field);
      if (hasValue(current)) return current;
    }
    return undefined;
  };

  const propName = displayValue(readProposalField('product_name'), '未命名新品');
  const propBrand = displayValue(readProposalField('brand'));
  const propCategory = displayValue(readProposalField('product_category_name', 'category'));
  const propCategoryCode = displayValue(readProposalField('product_category_code'), '');
  const propSubcategory = displayValue(
    readProposalField('product_subcategory_name', 'subcategory'),
  );
  const propSubcategoryCode = displayValue(readProposalField('product_subcategory_code'), '');
  const propSpec = displayValue(readProposalField('specification'));
  const propUnit = displayValue(readProposalField('sales_unit', 'unit'));
  const snapshotBarcodeValues = parseList(comparisonProposal?.barcodes)
    .map((item) =>
      typeof item === 'string'
        ? item
        : displayValue((item as Record<string, unknown>)?.barcode, ''),
    )
    .filter(Boolean);
  const propBarcodeValues = snapshotBarcodeValues.length
    ? snapshotBarcodeValues
    : selectedProposal?.product?.barcodes?.length
      ? selectedProposal.product.barcodes
      : [readProposalField('barcode')].filter(hasValue).map(String);
  const propBarcode = propBarcodeValues.length ? propBarcodeValues.join('、') : '—';
  const propExTax = readProposalField('ex_tax_price');
  const propVip = readProposalField('suggested_vip_price', 'vip_price');
  const propRetail = readProposalField('suggested_retail_price');
  const propGiveaway = readProposalField('gift_bundle_support_content', 'gifts', 'giveaway_terms');
  const propLaunchDate = readProposalField(
    'estimated_launch_date',
    'planned_launch_date',
    'expected_launch_date',
  );
  const propMonthlySales = readProposalField('estimated_monthly_sales', 'average_monthly_sales');
  const propAudience = readProposalField('target_audience', 'target_customers');
  const propRollout = readProposalField(
    'launch_rollout_notes',
    'initial_stock_suggestion',
    'suggested_store_type',
  );
  const propPromoSchedule = readProposalField(
    'promotion_schedule',
    'marketing_plan',
    'launch_plan',
  );
  const propFirstBatch = readProposalField(
    'first_batch_discount',
    'initial_order_offer_content',
    'initial_order_offer',
    'first_order_offer',
  );
  const propPromoSupport = readProposalField(
    'promotion_content',
    'promotional_support',
    'marketing_support',
    'promotion_support',
  );
  const propTrialSupport = readProposalField(
    'trial_support_content',
    'trial_support',
    'sample_support',
  );
  const propPromotionStart = readProposalField('promotion_start_date');
  const propPromotionEnd = readProposalField('promotion_end_date');
  const propMinimumOrderQuantity = readProposalField('minimum_order_quantity');
  const propMinimumOrderAmount = readProposalField('minimum_order_amount');
  const propFeatures = readProposalField('product_features', 'key_features');
  const propPositioning = readProposalField('market_positioning');
  const propDifference = readProposalField('competitor_difference');
  const propPriceRationale = readProposalField('suggested_price_rationale');
  const propMarketSales = readProposalField('market_sales', 'annual_sales_range');
  const propLaunchPlan = readProposalField('launch_plan');
  const propIngredients = readProposalField('ingredients', 'ingredient_text');
  const propShelfLife = readProposalField('shelf_life');
  const currentSalesChannels = (selectedProposal?.current_sales_channels || [])
    .map((item) => item.channel_name || item.channel_code)
    .filter(Boolean)
    .join('、');
  const expectedSalesChannels = (selectedProposal?.expected_sales_channels || [])
    .map((item) => item.channel_name || item.channel_code)
    .filter(Boolean)
    .join('、');

  const propCertStatus = getProposalField(selectedProposal, 'certificate_status');
  const propCertType = getProposalField(selectedProposal, 'certificate_type');
  const propCertNumber = getProposalField(selectedProposal, 'certificate_number');
  const propCertAttachmentUrl =
    getProposalField(selectedProposal, 'certificate_attachment_url') ||
    (() => {
      const match = selectedProposal?.attachments?.find((att: any) =>
        ['LICENSE', 'CERTIFICATION'].includes(att.type),
      );
      return match ? api.vendorProactive.downloadUrl(match.id, true) : null;
    })();
  const propCertNotRequiredReason = getProposalField(
    selectedProposal,
    'certificate_not_required_reason',
  );

  // 計算新品有效進價；搭贈文字無法可靠解析時保留 null，不以牌價冒充有效進價。
  const propEffectiveCostResult = calculateGiveawayEffectiveCost(propExTax, propGiveaway);
  const propEffectiveCost = propEffectiveCostResult.effectivePurchasePrice;
  const propUnitCost = calculateNormalizedUnitPrice(propExTax, propSpec, propUnit);
  const propUnitPrice = calculateNormalizedUnitPrice(propVip ?? propRetail, propSpec, propUnit);

  const marginResult = (cost: unknown, retail: unknown) => {
    if (!hasValue(cost) || !hasValue(retail)) return { amount: null, rate: null };
    return calculateGrossMargin({
      exTaxPrice: Number(cost),
      suggestedRetailPrice: Number(retail),
      taxRate: systemSettings.default_tax_rate,
      method: systemSettings.margin_calculation,
    });
  };
  const marginPercent = (cost: unknown, retail: unknown): number | null => {
    const rate = marginResult(cost, retail).rate;
    return rate === null ? null : Math.round(rate * 1000) / 10;
  };
  const propMarginBasePrice = propVip ?? propRetail;
  const proposalMargin = marginPercent(propExTax, propMarginBasePrice);
  const proposalEffectiveMargin = marginPercent(propEffectiveCost, propMarginBasePrice);

  // 正式提交的商品交易條件 Snapshot 優先，避免主檔日後變更影響會議依據。
  const rawTradeTerms =
    comparisonProposal?.trade_terms_snapshot ||
    selectedProposal?.product?.trade_terms ||
    selectedProposal?.trade_terms_source ||
    null;
  const terms = rawTradeTerms
    ? effectiveProductTradeTerms(rawTradeTerms as any)
    : ({} as Record<string, any>);

  // 主頁使用決策必要摘要；Drawer 開啟後則以即時正式廠商主檔為準。
  const supplierSummary = comparisonData?.supplier_summary || comparisonData?.supplier_master;
  const supplierMaster = supplierDetail || supplierSummary;
  const supplierFinancial =
    (typeof supplierMaster?.payment_terms === 'object' && supplierMaster?.payment_terms) ||
    supplierMaster?.financial_terms ||
    supplierMaster?.trade_terms ||
    {};
  const supplierDelivery =
    supplierMaster?.unified_delivery_terms || supplierMaster?.delivery_terms || {};
  const supplierReturns =
    supplierMaster?.unified_return_terms || supplierMaster?.return_terms || {};
  const supplierInvoice = supplierMaster?.invoice_terms || supplierMaster?.invoice_settings || {};
  const supplierPrimaryContact =
    supplierMaster?.contact_profile ||
    supplierMaster?.primary_contact ||
    supplierMaster?.contacts?.find((contact: any) => contact.contact_kind === 'PRIMARY') ||
    supplierMaster?.contacts?.[0] ||
    null;
  const supplierBrands = parseList(supplierMaster?.principal_brands)
    .map((item) =>
      typeof item === 'string'
        ? item
        : displayValue((item as Record<string, unknown>)?.brand_name, ''),
    )
    .filter(Boolean)
    .join('、');
  const suppliedCategories = parseList(supplierMaster?.supplied_product_categories);
  const supplierCategories = suppliedCategories.some((item) => item && typeof item === 'object')
    ? suppliedCategories
        .map((item) => {
          const value = item as Record<string, unknown>;
          const code = displayValue(value.category_code, '');
          const name = displayValue(value.category_name, '');
          return [code, name].filter(Boolean).join('｜');
        })
        .filter(Boolean)
        .join('、')
    : formatVendorCategories(suppliedCategories);
  const meetingSupplierSnapshot = meetingSnapshot?.supplier_decision_snapshot;
  const meetingSupplierFinancial = meetingSupplierSnapshot?.payment_terms || {};
  const meetingSupplierDelivery = meetingSupplierSnapshot?.delivery_terms || {};
  const meetingSupplierReturns = meetingSupplierSnapshot?.return_terms || {};

  const proposalReturnHandling = displayValue(terms.return_handling_method, '');
  const proposalReturnAllowed = !proposalReturnHandling
    ? '待確認'
    : proposalReturnHandling === 'NO_RETURN_EXCHANGE'
      ? '不可退換貨'
      : '依條件可退換貨';
  const returnRiskReasons: string[] = [];
  if (proposalReturnHandling === 'NO_RETURN_EXCHANGE') returnRiskReasons.push('新品不可退換貨');
  if (Number(propMinimumOrderQuantity) >= 24)
    returnRiskReasons.push(`最低訂購量為 ${propMinimumOrderQuantity}`);
  const shelfLifeMonths = Number(
    String(propShelfLife ?? '').match(/(\d+(?:\.\d+)?)\s*(?:個?月|months?)/i)?.[1],
  );
  if (Number.isFinite(shelfLifeMonths) && shelfLifeMonths > 0 && shelfLifeMonths <= 12)
    returnRiskReasons.push(`保存期限為 ${displayValue(propShelfLife)}`);
  const returnRiskLevel = !proposalReturnHandling
    ? '待確認'
    : returnRiskReasons.length >= 2
      ? '高風險'
      : returnRiskReasons.length === 1
        ? '中風險'
        : '低風險';
  const returnTermsDiffer =
    hasValue(terms.return_handling_method) &&
    hasValue(supplierSummary?.return_terms?.handling_method) &&
    (String(terms.return_handling_method) !==
      String(supplierSummary.return_terms.handling_method) ||
      JSON.stringify(terms.return_conditions || []) !==
        JSON.stringify(supplierSummary.return_terms.conditions || []));

  // 被取代競品資訊
  const replacedComp = competitors.find((c) => c.id === replacedProductId) || primaryComp;

  const moneyText = (value: unknown): string => {
    if (!hasValue(value) || !Number.isFinite(Number(value))) return '—';
    try {
      return new Intl.NumberFormat('zh-TW', {
        style: 'currency',
        currency: systemSettings.currency || 'TWD',
        maximumFractionDigits: 2,
      }).format(Number(value));
    } catch {
      return Number(value).toLocaleString('zh-TW', { maximumFractionDigits: 2 });
    }
  };
  const percentageText = (value: number | null): string => (value === null ? '—' : `${value}%`);
  const competitorCategory = (competitor: ProposalCompetitorDetail): string => {
    const value = competitor as any;
    const code = value.categoryCode || competitor.product_category_code;
    const name = value.categoryName || competitor.imported_category_name;
    return [code, name].filter(Boolean).join('｜') || '—';
  };
  const competitorSubcategory = (competitor: ProposalCompetitorDetail): string => {
    const value = competitor as any;
    const code = value.subcategoryCode || competitor.product_subcategory_code;
    const name = value.subcategoryName || competitor.imported_subcategory_name;
    return [code, name].filter(Boolean).join('｜') || '—';
  };
  const fullComparisonRows: Array<{
    group: string;
    label: string;
    proposal: string;
    competitor: (item: ProposalCompetitorDetail) => string;
  }> = [
    {
      group: '商品資料',
      label: '商品代號',
      proposal: displayValue(readProposalField('vendor_product_code')),
      competitor: (item) => displayValue(item.product_code || item.itemId),
    },
    {
      group: '商品資料',
      label: '商品名稱',
      proposal: propName,
      competitor: (item) => item.product_name,
    },
    {
      group: '商品資料',
      label: '品牌',
      proposal: propBrand,
      competitor: (item) => displayValue(item.brand),
    },
    {
      group: '商品資料',
      label: '商品分類',
      proposal: [propCategoryCode, propCategory].filter(Boolean).join('｜') || '—',
      competitor: competitorCategory,
    },
    {
      group: '商品資料',
      label: '商品子分類',
      proposal: [propSubcategoryCode, propSubcategory].filter(Boolean).join('｜') || '—',
      competitor: competitorSubcategory,
    },
    {
      group: '商品資料',
      label: '商品規格',
      proposal: propSpec,
      competitor: (item) => displayValue(item.specification),
    },
    {
      group: '商品資料',
      label: '銷售單位',
      proposal: propUnit,
      competitor: (item) => displayValue(item.sales_unit),
    },
    {
      group: '商品資料',
      label: '供應商',
      proposal: displayValue(selectedProposal?.vendor_name),
      competitor: (item) => displayValue((item as any).supplierName || item.imported_supplier_name),
    },
    {
      group: '商品資料',
      label: '國際條碼',
      proposal: propBarcode,
      competitor: (item) => displayValue((item as any).barcode || item.primary_barcode_candidate),
    },
    {
      group: '價格',
      label: '目前進價',
      proposal: moneyText(propExTax),
      competitor: (item) => moneyText(item.current_purchase_price),
    },
    {
      group: '價格',
      label: '平均成本',
      proposal: '—',
      competitor: (item) => moneyText(item.average_cost),
    },
    {
      group: '價格',
      label: '廠商搭贈',
      proposal: displayValue(propGiveaway),
      competitor: (item) => displayValue(item.current_vendor_giveaway_terms),
    },
    {
      group: '價格',
      label: '搭贈後有效進價',
      proposal: propEffectiveCost === null ? '有效進價待確認' : moneyText(propEffectiveCost),
      competitor: (item) => {
        const value = calculateGiveawayEffectiveCost(
          item.current_purchase_price ?? item.average_cost,
          item.current_vendor_giveaway_terms,
        ).effectivePurchasePrice;
        return value === null ? '有效進價待確認' : moneyText(value);
      },
    },
    {
      group: '價格',
      label: 'VIP 價',
      proposal: moneyText(propVip),
      competitor: (item) => moneyText(item.vip_price),
    },
    {
      group: '價格',
      label: '一般售價',
      proposal: moneyText(propRetail),
      competitor: (item) => moneyText(item.regular_sale_price),
    },
    {
      group: '價格',
      label: '員工價',
      proposal: '—',
      competitor: (item) => moneyText(item.employee_price),
    },
    {
      group: '價格',
      label: '單位成本',
      proposal: propUnitCost.display,
      competitor: (item) =>
        calculateNormalizedUnitPrice(
          item.current_purchase_price ?? item.average_cost,
          item.specification,
          item.sales_unit,
        ).display,
    },
    {
      group: '價格',
      label: '單位售價',
      proposal: propUnitPrice.display,
      competitor: (item) =>
        calculateNormalizedUnitPrice(
          item.vip_price ?? item.regular_sale_price,
          item.specification,
          item.sales_unit,
        ).display,
    },
    {
      group: '價格',
      label: '毛利額',
      proposal: moneyText(marginResult(propExTax, propMarginBasePrice).amount),
      competitor: (item) =>
        moneyText(
          marginResult(
            item.current_purchase_price ?? item.average_cost,
            item.vip_price ?? item.regular_sale_price,
          ).amount,
        ),
    },
    {
      group: '價格',
      label: '毛利率',
      proposal: percentageText(proposalMargin),
      competitor: (item) =>
        percentageText(
          marginPercent(
            item.current_purchase_price ?? item.average_cost,
            item.vip_price ?? item.regular_sale_price,
          ),
        ),
    },
    {
      group: '營運',
      label: '目前庫存',
      proposal: '0（尚未進貨）',
      competitor: (item) => displayValue(item.warehouse_stock),
    },
    {
      group: '營運',
      label: '已訂未到',
      proposal: '0',
      competitor: (item) => displayValue(item.on_order_quantity),
    },
    {
      group: '營運',
      label: '是否可退貨',
      proposal: proposalReturnAllowed,
      competitor: (item) => displayValue(item.return_allowed, '待確認'),
    },
    {
      group: '營運',
      label: '退換貨條件',
      proposal: displayValue(terms.return_description),
      competitor: (item) => displayValue(item.return_conditions),
    },
    {
      group: '營運',
      label: '商品狀態',
      proposal: '新品提案',
      competitor: (item) => displayValue(item.status_hint || item.classification_status),
    },
    {
      group: '商品內容',
      label: '主要成分／含量',
      proposal: displayValue(propIngredients),
      competitor: formatIngredientSummary,
    },
    {
      group: '商品內容',
      label: '成分來源',
      proposal: '新品提案',
      competitor: (item) => displayValue(item.ingredient_source_name),
    },
    {
      group: '商品內容',
      label: '主要特色',
      proposal: displayValue(propFeatures),
      competitor: (item) => displayValue(item.product_features || item.features),
    },
    {
      group: '商品內容',
      label: '適用客群',
      proposal: displayValue(propAudience),
      competitor: (item) => displayValue(item.target_audience),
    },
    {
      group: '商品內容',
      label: '市場定位',
      proposal: displayValue(propPositioning),
      competitor: (item) => displayValue(item.market_positioning),
    },
  ];

  return (
    <div className="space-y-6 pb-40">
      <PageHeader
        eyebrow="MEETING DECISION ROOM"
        title="新品決策會議模式（單頁標準決策流程）"
        description="依採購決策順序一站式呈現 11 項核心資料，包含有效進價量化、1～5 項競品決策卡、供應商主檔速查、既有庫存去化決策與會議決議。"
      />

      {/* 待會議審查提案選擇列 */}
      <Card className="p-4 bg-slate-900 text-white shadow-xl">
        <div className="grid gap-4 md:grid-cols-3 items-center">
          <div>
            <label className="block text-xs font-bold text-teal-300 mb-1">
              待會議審查提案清單 ({candidates.length})
            </label>
            <select
              className="w-full bg-slate-800 border border-slate-700 text-white text-xs rounded-xl p-2.5 focus:outline-none focus:border-teal-400"
              value={selectedId}
              onChange={(e) => handleSelect(e.target.value)}
              disabled={loadingList}
            >
              {candidates.length === 0 && <option value="">無待會議審查案件</option>}
              {candidates.map((p) => {
                const no = p.proposal_no || (p as any).proposal_number || p.id;
                const name = (p as any).product_name || p.product?.name_zh || '新品提案';
                return (
                  <option key={p.id} value={p.id}>
                    [{no}] {name} ({p.vendor_name || '廠商'})
                  </option>
                );
              })}
            </select>
          </div>

          <div className="md:col-span-2 flex flex-wrap justify-between items-center gap-2 text-xs">
            {selectedProposal && (
              <div>
                <span className="text-white/60">目前審核商品：</span>
                <strong className="text-teal-300 text-sm ml-1">{propName}</strong>
                <span className="text-white/40 ml-2">({propBrand})</span>
                <span className="ml-3 rounded-full bg-teal-800/80 px-2 py-0.5 text-[11px] font-mono text-teal-200 border border-teal-600">
                  競品 {competitors.length} / 5
                </span>
              </div>
            )}
            {selectedProposal && (
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  className="text-xs bg-slate-800 text-teal-300 border-slate-700 hover:bg-slate-700"
                  onClick={() => setShowFullCompareModal(true)}
                >
                  📊 完整比較彈窗 (1+{competitors.length})
                </Button>
                <Link to={`/compare?proposal_id=${selectedProposal.id}`}>
                  <Button
                    variant="secondary"
                    className="text-xs bg-slate-800 text-teal-300 border-slate-700 hover:bg-slate-700"
                  >
                    🔍 編輯競品清單 →
                  </Button>
                </Link>
              </div>
            )}
          </div>
        </div>
      </Card>

      {loadingDetail ? (
        <Loading label="載入會議資料與多競品快照中…" />
      ) : !selectedProposal ? (
        <EmptyState
          title="目前無待審查的新品提案"
          description="當採購將新品提案狀態推進至「待新品會議審查」時，將顯示於此。"
        />
      ) : (
        <div className="space-y-6">
          {/* ======================================================== */}
          {/* 1. 新品基本資料與供應商資訊 (含許可證與 Drawer 觸發) */}
          {/* ======================================================== */}
          <Card className="p-6 border-teal-200 bg-teal-50/20">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-teal-100 pb-3 mb-4 gap-2">
              <div>
                <span className="text-xs font-bold text-teal-700 uppercase tracking-wide">
                  1. 新品基本資料與供應商
                </span>
                <h2 className="text-xl font-extrabold text-ink mt-0.5">{propName}</h2>
                <div className="flex flex-wrap items-center gap-2 text-xs text-slate-600 mt-1">
                  <span>
                    品牌：<strong>{propBrand}</strong>
                  </span>
                  <span>｜</span>
                  <span>
                    大類：<strong>{propCategory}</strong>
                  </span>
                  <span>｜</span>
                  <span>
                    子類：<strong>{propSubcategory}</strong>
                  </span>
                  <span>｜</span>
                  <span>
                    規格：<strong>{propSpec}</strong>
                  </span>
                  <span>｜</span>
                  <span>
                    單位：<strong>{propUnit}</strong>
                  </span>
                  <span>｜</span>
                  <span>
                    條碼：<strong className="font-mono">{propBarcode}</strong>
                  </span>
                </div>
              </div>

              <div className="text-right flex sm:flex-col items-center sm:items-end justify-between gap-2">
                <button
                  type="button"
                  onClick={handleOpenSupplierDrawer}
                  className="px-3 py-1.5 bg-white border border-teal-300 text-teal-800 hover:bg-teal-50 rounded-xl text-xs font-bold shadow-2xs transition flex items-center gap-1.5"
                >
                  <span>🏢 供應商：{selectedProposal.vendor_name || '查看供應商'}</span>
                  <span className="text-teal-600 font-normal">（速查主檔 ↗）</span>
                </button>
                <div className="text-xs font-mono text-slate-500">
                  提案號：{getProposalField(selectedProposal, 'proposal_number')}
                </div>
              </div>
            </div>

            {/* 認證／許可證合規區塊 */}
            <div className="p-3.5 bg-white rounded-xl border border-teal-100 text-xs">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <span className="font-bold text-slate-700">主管機關許可證：</span>
                  {propCertStatus === 'HAS_CERTIFICATE' ? (
                    <>
                      <span className="px-2 py-0.5 rounded bg-teal-100 text-teal-800 font-bold">
                        ✓ {propCertType ? labelCertificateType(propCertType) : '有證'}
                      </span>
                      <span className="font-mono font-bold text-slate-800">
                        {propCertNumber || '—'}
                      </span>
                    </>
                  ) : propCertStatus === 'NOT_REQUIRED' ? (
                    <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-600 font-medium">
                      依法免認證／許可證（{displayValue(propCertNotRequiredReason, '未填寫原因')}）
                    </span>
                  ) : (
                    <span className="px-2 py-0.5 rounded bg-amber-100 text-amber-800 font-bold">
                      ⚠️ 未提供認證資料
                    </span>
                  )}
                </div>

                {propCertAttachmentUrl && (
                  <a
                    href={propCertAttachmentUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="font-bold text-teal-700 hover:underline inline-flex items-center gap-1"
                  >
                    👁️ 檢視主管機關許可證明文件 ↗
                  </a>
                )}
              </div>
            </div>

            <div className="mt-3 grid gap-2 rounded-xl border border-teal-100 bg-white p-3 text-xs sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <span className="block text-slate-400">供應商</span>
                <strong>
                  {displayValue(supplierSummary?.name || selectedProposal.vendor_name)}
                </strong>
              </div>
              <div>
                <span className="block text-slate-400">付款條件</span>
                <strong>
                  {hasValue(supplierSummary?.financial_terms?.payment_term)
                    ? `${labelVendorPaymentTerm(supplierSummary.financial_terms.payment_term)}${
                        supplierSummary.financial_terms.monthly_payment_days
                          ? ` ${supplierSummary.financial_terms.monthly_payment_days} 天`
                          : ''
                      }`
                    : '—'}
                </strong>
              </div>
              <div>
                <span className="block text-slate-400">配送</span>
                <strong>
                  {hasValue(supplierSummary?.delivery_terms?.delivery_scope)
                    ? labelVendorDeliveryScope(supplierSummary.delivery_terms.delivery_scope)
                    : '—'}
                </strong>
              </div>
              <div>
                <span className="block text-slate-400">退貨</span>
                <strong>
                  {hasValue(supplierSummary?.return_terms?.handling_method)
                    ? labelVendorReturnHandling(supplierSummary.return_terms.handling_method)
                    : '—'}
                </strong>
              </div>
              <div>
                <span className="block text-slate-400">主要聯絡人</span>
                <strong>{displayValue(supplierSummary?.primary_contact?.name)}</strong>
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 2. 價格與交易條件 (含有效進價與等效折讓) */}
          {/* ======================================================== */}
          <Card className="p-6">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-4 flex items-center justify-between">
              <span>💰 2. 價格試算與交易條件</span>
              <span className="text-xs text-slate-400 font-normal">
                精準解析搭贈文字，量化有效進價與折讓率
              </span>
            </h3>

            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">牌價進價 (未稅)</span>
                <span className="text-xl font-extrabold text-ink mt-1 block">
                  <Money value={propExTax} />
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  單位進價：{propUnitPrice.display}
                </span>
              </div>

              <div className="p-3.5 bg-emerald-50/50 rounded-xl border border-emerald-200">
                <span className="text-emerald-800 block font-bold">⭐ 搭贈後有效進價</span>
                <span className="text-xl font-extrabold text-emerald-700 mt-1 block">
                  {propEffectiveCost === null ? (
                    '有效進價待確認'
                  ) : (
                    <Money value={propEffectiveCost} />
                  )}
                </span>
                <span className="text-[11px] text-emerald-600 block mt-1">
                  {propEffectiveCostResult.isCalculated
                    ? `搭贈 ${propGiveaway}（等效折讓 ${propEffectiveCostResult.discountPercentage}%）`
                    : propEffectiveCostResult.display}
                </span>
              </div>

              <div className="p-3.5 bg-teal-50/50 rounded-xl border border-teal-200">
                <span className="text-teal-800 block font-bold">建議 VIP 價 / 建議售價</span>
                <span className="text-xl font-extrabold text-teal-700 mt-1 block">
                  <Money value={propVip} />
                  <span className="text-xs text-slate-400 ml-1 font-normal">
                    / <Money value={propRetail} />
                  </span>
                </span>
                <span className="text-[11px] text-teal-700 block mt-1 font-semibold">
                  牌價毛利：{proposalMargin != null ? `${proposalMargin}%` : '—'}
                  {proposalEffectiveMargin != null && (
                    <> ｜ 搭贈後毛利：{proposalEffectiveMargin}%</>
                  )}
                </span>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <span className="text-slate-400 block font-bold">交易結帳與配送週期</span>
                <span className="font-bold text-slate-800 mt-1 block">
                  {hasValue(terms.payment_term)
                    ? `${labelVendorPaymentTerm(terms.payment_term)}${
                        terms.monthly_payment_days ? ` ${terms.monthly_payment_days} 天` : ''
                      }`
                    : '—'}{' '}
                  ｜{' '}
                  {hasValue(terms.payment_method)
                    ? labelVendorPaymentMethod(terms.payment_method)
                    : '—'}
                </span>
                <span className="text-[11px] text-slate-500 block mt-1">
                  最低訂購量：{displayValue(propMinimumOrderQuantity)} ｜ 最低訂購金額：{' '}
                  <Money value={propMinimumOrderAmount as any} />
                </span>
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 3. 退換貨條件 (標示來源與風險原因) */}
          {/* ======================================================== */}
          <Card className="p-6">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-3">
              🔄 3. 退換貨條件與風險評估
            </h3>
            <div className="grid gap-3 md:grid-cols-3 text-xs">
              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-bold text-slate-700">本次提案退換貨約定：</span>
                  <span className="px-1.5 py-0.2 bg-teal-100 text-teal-800 rounded text-[10px] font-bold">
                    來源：[新品提案]
                  </span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  是否可退換：<strong>{proposalReturnAllowed}</strong>
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  處理方式：
                  {hasValue(terms.return_handling_method)
                    ? labelVendorReturnHandling(terms.return_handling_method)
                    : '—'}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  成立條件：{formatVendorReturnConditions(terms.return_conditions)}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  詳細說明：{displayValue(terms.return_description)}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  運費負擔：
                  {hasValue(terms.return_freight_responsibility)
                    ? labelVendorReturnFreight(terms.return_freight_responsibility)
                    : '—'}
                </p>
              </div>

              <div className="p-3.5 bg-slate-50 rounded-xl border border-slate-100">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="font-bold text-slate-700">供應商正式主檔退貨機制：</span>
                  <span className="px-1.5 py-0.2 bg-slate-200 text-slate-700 rounded text-[10px] font-bold">
                    來源：[供應商主檔]
                  </span>
                </div>
                <p className="text-slate-700 leading-relaxed">
                  處理方式：
                  {hasValue(supplierSummary?.return_terms?.handling_method)
                    ? labelVendorReturnHandling(supplierSummary.return_terms.handling_method)
                    : '—'}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  成立條件：
                  {formatVendorReturnConditions(supplierSummary?.return_terms?.conditions)}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  詳細說明：
                  {displayValue(supplierSummary?.return_terms?.condition_description)}
                </p>
                <p className="text-slate-600 mt-1 leading-relaxed">
                  運費責任：
                  {hasValue(supplierSummary?.return_terms?.freight_responsibility)
                    ? labelVendorReturnFreight(supplierSummary.return_terms.freight_responsibility)
                    : '—'}
                </p>
              </div>

              <div
                className={`p-3.5 rounded-xl border ${
                  returnRiskLevel === '高風險'
                    ? 'border-rose-200 bg-rose-50'
                    : returnRiskLevel === '中風險'
                      ? 'border-amber-200 bg-amber-50'
                      : 'border-emerald-200 bg-emerald-50'
                }`}
              >
                <span className="font-bold text-slate-800">退換貨風險：{returnRiskLevel}</span>
                {returnRiskReasons.length > 0 ? (
                  <ul className="mt-2 list-disc space-y-1 pl-4 text-slate-700">
                    {returnRiskReasons.map((reason) => (
                      <li key={reason}>{reason}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-slate-600">
                    {returnRiskLevel === '待確認'
                      ? '尚缺新品退換貨資料，無法判定風險。'
                      : '目前資料未觸發既定風險因素。'}
                  </p>
                )}
                {hasValue(meetingComparison?.return_risk_notes) && (
                  <p className="mt-2 border-t border-current/10 pt-2 text-slate-700">
                    採購補充：{meetingComparison?.return_risk_notes}
                  </p>
                )}
              </div>
              {returnTermsDiffer && (
                <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 font-bold text-amber-900 md:col-span-3">
                  新品條件與供應商一般退換貨條件不同，請以兩個來源分別確認，不會自動互相覆蓋。
                </div>
              )}
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 4. 惠生主要競品決策卡 */}
          {/* ======================================================== */}
          <Card className="p-6 border-amber-200 bg-amber-50/30">
            <div className="flex items-center justify-between border-b border-amber-200 pb-3 mb-4">
              <div>
                <span className="text-xs font-bold text-amber-800 uppercase tracking-wide">
                  4. 惠生主要競品決策卡
                </span>
                <h3 className="text-base font-bold text-ink mt-0.5">
                  {primaryComp ? primaryComp.product_name : '尚未指定主要競品'}
                </h3>
              </div>
              {primaryComp && (
                <span className="font-mono text-xs font-bold bg-amber-100 text-amber-900 px-2.5 py-1 rounded-lg border border-amber-300">
                  代號：{primaryComp.product_code || primaryComp.itemId}
                </span>
              )}
            </div>

            {primaryComp ? (
              <div className="space-y-3 text-xs">
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 bg-white p-4 rounded-xl border border-amber-100">
                  <div>
                    <span className="text-slate-400 block font-bold">主要競品規格與單位</span>
                    <strong className="text-ink text-sm">
                      {primaryComp.specification || '—'} ｜ {primaryComp.sales_unit || '盒'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">競品進價 / VIP 價</span>
                    <strong className="text-ink text-sm">
                      <Money
                        value={primaryComp.current_purchase_price ?? primaryComp.average_cost}
                      />{' '}
                      / <Money value={primaryComp.vip_price ?? primaryComp.regular_sale_price} />
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">新品 / 競品有效進價</span>
                    <strong className="text-emerald-700 text-sm">
                      {propEffectiveCost === null ? (
                        '新品待確認'
                      ) : (
                        <Money value={propEffectiveCost} />
                      )}{' '}
                      /{' '}
                      {primaryComp.effective_cost === null ||
                      primaryComp.effective_cost === undefined ? (
                        '競品待確認'
                      ) : (
                        <Money value={primaryComp.effective_cost} />
                      )}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">新品 / 競品毛利率</span>
                    <strong className="text-teal-800 text-sm">
                      {proposalMargin === null ? '—' : `${proposalMargin}%`} /{' '}
                      {marginPercent(
                        primaryComp.current_purchase_price ?? primaryComp.average_cost,
                        primaryComp.vip_price ?? primaryComp.regular_sale_price,
                      ) === null
                        ? '—'
                        : `${marginPercent(
                            primaryComp.current_purchase_price ?? primaryComp.average_cost,
                            primaryComp.vip_price ?? primaryComp.regular_sale_price,
                          )}%`}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">現有庫存 / 在途量</span>
                    <strong className="text-slate-800 text-sm">
                      {primaryComp.warehouse_stock ?? 0} {primaryComp.sales_unit || '件'}
                      {(primaryComp.on_order_quantity || 0) > 0 &&
                        ` (在途 +${primaryComp.on_order_quantity})`}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">新品 / 競品退貨</span>
                    <strong className="text-slate-800 text-sm">
                      {proposalReturnAllowed} / {displayValue(primaryComp.return_allowed, '待確認')}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-bold">採購建議決策</span>
                    <strong className="text-teal-700 font-bold text-sm">
                      {COMPARISON_REPLACEMENT_LABELS[
                        meetingComparison?.replacement_assessment || ''
                      ] || '待會議討論'}
                    </strong>
                  </div>
                </div>

                {(meetingComparison?.multi_competitor_summary ||
                  comparisonData?.multi_competitor_summary) && (
                  <div className="p-3 bg-white/90 rounded-xl border border-amber-200">
                    <span className="font-bold text-amber-900 block mb-1">🤖 系統比對摘要：</span>
                    <p className="text-slate-700 leading-relaxed">
                      {meetingComparison?.multi_competitor_summary ||
                        comparisonData?.multi_competitor_summary}
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <div className="text-center py-4 text-slate-500 text-xs">
                尚未設定主要競品。請至「新品比較」設定主要競品以供會議審查。
              </div>
            )}
          </Card>

          {/* ======================================================== */}
          {/* 5. 其他惠生競品摘要 (最多 4 個小型卡片) */}
          {/* ======================================================== */}
          {secondaryComps.length > 0 && (
            <Card className="p-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-4">
                <div>
                  <span className="text-xs font-bold text-slate-500 uppercase">
                    5. 其他惠生內部競品摘要
                  </span>
                  <h3 className="text-base font-bold text-ink">
                    次要競品清單（共 {secondaryComps.length} 項）
                  </h3>
                </div>
                <Button
                  variant="secondary"
                  className="text-xs"
                  onClick={() => setShowFullCompareModal(true)}
                >
                  查看完整 1+{competitors.length} 比較表 ↗
                </Button>
              </div>

              <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
                {secondaryComps.map((comp, idx) => {
                  const eff = calculateGiveawayEffectiveCost(
                    comp.current_purchase_price ?? comp.average_cost,
                    comp.current_vendor_giveaway_terms,
                  );
                  const competitorSalePrice = comp.vip_price ?? comp.regular_sale_price;
                  const competitorMargin = marginPercent(
                    comp.current_purchase_price ?? comp.average_cost,
                    competitorSalePrice,
                  );
                  const proposalSalePrice = propVip ?? propRetail;
                  const priceDifference =
                    hasValue(proposalSalePrice) && hasValue(competitorSalePrice)
                      ? Number(proposalSalePrice) - Number(competitorSalePrice)
                      : null;
                  return (
                    <div
                      key={comp.id || idx}
                      className="p-3.5 bg-slate-50 rounded-xl border border-slate-200 hover:border-teal-300 transition"
                    >
                      <div className="flex justify-between items-start">
                        <span className="font-mono text-teal-800 font-bold text-[11px]">
                          {comp.product_code || comp.itemId}
                        </span>
                        <span className="px-1.5 py-0.2 bg-slate-200 text-slate-600 rounded text-[10px]">
                          次要競品
                        </span>
                      </div>
                      <h4
                        className="font-bold text-ink text-sm mt-1 truncate"
                        title={comp.product_name}
                      >
                        {comp.product_name}
                      </h4>
                      <p className="text-slate-500 text-[11px] truncate">
                        規格：{comp.specification || '—'} ｜ 庫存：{comp.warehouse_stock ?? 0}
                      </p>
                      <div className="mt-2 pt-2 border-t border-slate-200 grid grid-cols-2 gap-2 text-[11px]">
                        <div>
                          <span className="text-slate-400 block">進價</span>
                          <span className="font-bold text-ink">
                            <Money value={comp.current_purchase_price ?? comp.average_cost} />
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">VIP 價</span>
                          <span className="font-bold text-teal-700">
                            <Money value={comp.vip_price ?? comp.regular_sale_price} />
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">有效進價</span>
                          <span className="font-bold text-emerald-700">
                            {eff.effectivePurchasePrice === null ? (
                              '待確認'
                            ) : (
                              <Money value={eff.effectivePurchasePrice} />
                            )}
                          </span>
                        </div>
                        <div>
                          <span className="text-slate-400 block">毛利 / 庫存</span>
                          <span className="font-bold text-slate-700">
                            {competitorMargin === null ? '—' : `${competitorMargin}%`} /{' '}
                            {comp.warehouse_stock ?? 0}
                          </span>
                        </div>
                      </div>
                      <div className="mt-2 flex items-center justify-between text-[11px] text-slate-600">
                        <span>
                          與新品售價差：
                          {priceDifference === null ? '—' : <Money value={priceDifference} />}
                        </span>
                        <button
                          type="button"
                          className="font-bold text-teal-700 hover:underline"
                          onClick={() => setShowFullCompareModal(true)}
                        >
                          查看詳細比較
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </Card>
          )}

          {/* ======================================================== */}
          {/* 6. 成分／市場價格比較 (外部行情) */}
          {/* ======================================================== */}
          <Card className="p-6">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-4 flex items-center justify-between">
              <span>🔬 6. 成分與外部市場價格觀測</span>
              {comparisonData?.web_price_stats.count ? (
                <span className="text-xs text-sky-800 font-mono bg-sky-50 px-2.5 py-1 rounded-lg border border-sky-200">
                  外部均價：
                  <Money value={comparisonData.web_price_stats.avg_price} /> ｜ 中位價：
                  <Money value={comparisonData.web_price_stats.median_price} />
                </span>
              ) : null}
            </h3>

            <div className="grid gap-3 sm:grid-cols-2 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">
                  新品主要成分標示：
                  <span className="px-1.5 py-0.2 ml-1 bg-teal-100 text-teal-800 rounded text-[10px]">
                    [新品提案]
                  </span>
                </span>
                <p className="text-slate-700 leading-relaxed">
                  {displayValue(propIngredients, '尚未提供成分資料')}
                </p>
              </div>

              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">
                  主要競品成分資料：
                  {hasValue(primaryComp?.ingredient_source_name) && (
                    <span className="px-1.5 py-0.2 ml-1 bg-slate-200 text-slate-700 rounded text-[10px]">
                      [{primaryComp?.ingredient_source_name}]
                    </span>
                  )}
                </span>
                <p className="text-slate-700 leading-relaxed">
                  {formatIngredientSummary(primaryComp) === '—'
                    ? '尚未建立有來源的成分資料'
                    : formatIngredientSummary(primaryComp)}
                </p>
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 7. 銷售與上市資料 */}
          {/* ======================================================== */}
          <Card className="p-6">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-4">
              📊 7. 銷售與上市規劃
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">商品主要特色</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propFeatures)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">商品市場定位</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propPositioning)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">主要消費客群</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propAudience)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">與競品差異</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propDifference)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">建議售價理由</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propPriceRationale)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">市場銷售實績</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propMarketSales)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">目前／預計銷售通路</span>
                <p className="text-slate-600 leading-relaxed">
                  目前：{displayValue(currentSalesChannels)}
                  <br />
                  預計：{displayValue(expectedSalesChannels)}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl">
                <span className="font-bold text-slate-700 block mb-1">上市日期／月銷量</span>
                <p className="text-slate-600 leading-relaxed">
                  {displayValue(propLaunchDate)} ｜{' '}
                  {hasValue(propMonthlySales) ? `${propMonthlySales} 件` : '—'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl sm:col-span-2">
                <span className="font-bold text-slate-700 block mb-1">首批鋪貨與門市建議</span>
                <p className="text-slate-600 leading-relaxed">{displayValue(propRollout)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl sm:col-span-2">
                <span className="font-bold text-slate-700 block mb-1">上市推廣計畫</span>
                <p className="text-slate-600 leading-relaxed">
                  {displayValue(propLaunchPlan || propPromoSchedule)}
                </p>
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 8. 優惠與行銷支援 */}
          {/* ======================================================== */}
          <Card className="p-6">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-4">
              🎁 8. 廠商搭贈、採購優惠與行銷支援
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4 text-xs">
              <div className="p-3.5 bg-emerald-50/50 border border-emerald-100 rounded-xl">
                <span className="font-bold text-emerald-800 block mb-1">贈品及搭贈</span>
                <p className="text-slate-700 leading-relaxed">{displayValue(propGiveaway)}</p>
                <p className="mt-2 text-[11px] text-emerald-700">
                  有效進價：
                  {propEffectiveCost === null ? '待確認' : <Money value={propEffectiveCost} />}
                  {propEffectiveCostResult.isCalculated && (
                    <> ｜ 等效折讓 {propEffectiveCostResult.discountPercentage}%</>
                  )}
                </p>
              </div>
              <div className="p-3.5 bg-teal-50/40 border border-teal-100 rounded-xl">
                <span className="font-bold text-teal-800 block mb-1">首批進貨優惠</span>
                <p className="text-slate-700 leading-relaxed">{displayValue(propFirstBatch)}</p>
              </div>
              <div className="p-3.5 bg-teal-50/40 border border-teal-100 rounded-xl">
                <span className="font-bold text-teal-800 block mb-1">促銷補貼與贊助</span>
                <p className="text-slate-700 leading-relaxed">{displayValue(propPromoSupport)}</p>
                <p className="mt-2 text-[11px] text-slate-500">
                  期間：{displayValue(propPromotionStart)} ～ {displayValue(propPromotionEnd)}
                </p>
              </div>
              <div className="p-3.5 bg-teal-50/40 border border-teal-100 rounded-xl">
                <span className="font-bold text-teal-800 block mb-1">體驗品與試用包</span>
                <p className="text-slate-700 leading-relaxed">{displayValue(propTrialSupport)}</p>
              </div>
              <div className="p-3.5 bg-slate-50 border border-slate-100 rounded-xl sm:col-span-2 lg:col-span-4">
                <span className="font-bold text-slate-700">採購門檻：</span>
                最低訂購量 {displayValue(propMinimumOrderQuantity)} ｜ 最低訂購金額{' '}
                <Money value={propMinimumOrderAmount as any} />
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 9. 採購結論 */}
          {/* ======================================================== */}
          <Card className="p-6 border-slate-200">
            <h3 className="text-base font-bold text-ink border-b border-slate-100 pb-3 mb-4">
              📝 9. 採購前置比對結論與建議
            </h3>
            <div className="grid gap-3 sm:grid-cols-2 text-xs">
              <div className="p-3 bg-emerald-50/40 rounded-xl border border-emerald-100">
                <span className="font-bold text-emerald-900 block mb-1">新品主要優勢：</span>
                <p className="text-slate-700 leading-relaxed">
                  {meetingComparison?.buyer_advantages || '尚未填寫優勢分析'}
                </p>
              </div>
              <div className="p-3 bg-rose-50/40 rounded-xl border border-rose-100">
                <span className="font-bold text-rose-900 block mb-1">新品主要劣勢與風險：</span>
                <p className="text-slate-700 leading-relaxed">
                  {meetingComparison?.buyer_disadvantages || '尚未填寫劣勢分析'}
                </p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100 sm:col-span-2">
                <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                  <div>
                    價格具競爭力：
                    <strong>{comparisonFlagLabel(meetingComparison?.is_price_competitive)}</strong>
                  </div>
                  <div>
                    成分具競爭力：
                    <strong>
                      {comparisonFlagLabel(meetingComparison?.is_ingredient_superior)}
                    </strong>
                  </div>
                  <div>
                    毛利具競爭力：
                    <strong>{comparisonFlagLabel(meetingComparison?.is_margin_superior)}</strong>
                  </div>
                  <div>
                    搭贈具優勢：
                    <strong>{comparisonFlagLabel(meetingComparison?.is_giveaway_superior)}</strong>
                  </div>
                </div>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="font-bold text-slate-700 block mb-1">退換貨風險</span>
                <p>{displayValue(meetingComparison?.return_risk_notes)}</p>
              </div>
              <div className="p-3 bg-slate-50 rounded-xl border border-slate-100">
                <span className="font-bold text-slate-700 block mb-1">庫存替代風險</span>
                <p>{displayValue(meetingComparison?.inventory_replacement_risk_notes)}</p>
              </div>
              <div className="p-3 bg-teal-50 rounded-xl border border-teal-100 sm:col-span-2">
                <span className="font-bold text-teal-900 block mb-1">採購建議</span>
                <p>
                  {COMPARISON_REPLACEMENT_LABELS[meetingComparison?.replacement_assessment || ''] ||
                    '待會議決議'}
                </p>
                <p className="mt-1 text-slate-600">
                  {displayValue(meetingComparison?.buyer_conclusion_notes)}
                </p>
              </div>
            </div>
          </Card>

          {/* ======================================================== */}
          {/* 10. 既有商品庫存影響與去化決策提示 */}
          {/* ======================================================== */}
          <Card className="p-6 border-amber-200 bg-amber-50/20">
            <h3 className="text-base font-bold text-ink border-b border-amber-200 pb-3 mb-3 flex items-center justify-between">
              <span>⚠️ 10. 既有商品庫存影響與去化決策</span>
              <span className="text-xs text-amber-800 font-bold">
                決議取代舊品時必須評估庫存處理
              </span>
            </h3>

            {replacedComp ? (
              <div className="space-y-3 text-xs">
                <div className="p-3.5 bg-white rounded-xl border border-amber-200 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <span className="text-slate-400 block">被取代／相關競品</span>
                    <strong className="text-ink text-sm">
                      {replacedComp.product_name} (
                      {replacedComp.product_code || replacedComp.itemId})
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block">目前總倉庫存</span>
                    <strong className="text-rose-700 text-sm">
                      {replacedComp.warehouse_stock ?? 0} {replacedComp.sales_unit || '件'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block">在途未到量</span>
                    <strong className="text-slate-700 text-sm">
                      {replacedComp.on_order_quantity ?? 0} {replacedComp.sales_unit || '件'}
                    </strong>
                  </div>
                  <div>
                    <span className="text-slate-400 block">庫存去化總量</span>
                    <strong className="text-amber-900 text-sm font-bold">
                      {(replacedComp.warehouse_stock || 0) + (replacedComp.on_order_quantity || 0)}{' '}
                      {replacedComp.sales_unit || '件'}
                    </strong>
                  </div>
                </div>

                <p className="text-slate-600 text-xs">
                  💡
                  建議於決議上市時，明確指定「售完不補」、「促銷去化」或「退回供應商」，避免門市雙重庫存積壓。
                </p>
              </div>
            ) : (
              <div className="text-xs text-slate-500">
                本新品為全新獨立品項，不影響既有商品庫存。
              </div>
            )}
          </Card>

          {/* ======================================================== */}
          {/* 11. 會議完整度 Checklist */}
          {/* ======================================================== */}
          {comparisonData?.readiness && (
            <Card className="p-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-3 mb-3">
                <h3 className="text-base font-bold text-ink">📋 11. 會議資料審查完整度檢查</h3>
                <span className="text-xs font-mono font-bold text-teal-800 bg-teal-50 px-2.5 py-1 rounded-lg border border-teal-200">
                  完成度：{comparisonData.readiness.percentage}%
                </span>
              </div>
              <div className="grid gap-2 sm:grid-cols-3 text-xs">
                {comparisonData.readiness.checklist.map((item, idx) => (
                  <div
                    key={idx}
                    className={`p-2.5 rounded-lg border flex items-center gap-2 ${
                      item.done
                        ? 'bg-emerald-50/50 border-emerald-200 text-emerald-900'
                        : 'bg-slate-50 border-slate-200 text-slate-500'
                    }`}
                  >
                    <span>{item.done ? '✓' : '○'}</span>
                    <span className="font-medium">{item.label}</span>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* ======================================================== */}
      {/* 供應商完整資訊 Drawer / Modal */}
      {/* ======================================================== */}
      {showSupplierDrawer && (
        <div className="fixed inset-0 z-50 flex justify-end bg-black/40 backdrop-blur-xs transition-opacity">
          <div className="w-full max-w-2xl bg-white h-full shadow-2xl overflow-y-auto p-6 flex flex-col justify-between">
            <div className="space-y-6">
              <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                <div>
                  <span className="text-xs font-bold text-teal-700 uppercase">
                    目前正式供應商主檔
                  </span>
                  <h3 className="text-xl font-extrabold text-ink mt-0.5">
                    {displayValue(
                      supplierMaster?.name_zh ||
                        supplierMaster?.name ||
                        selectedProposal?.vendor_name,
                      '供應商基本資料',
                    )}
                  </h3>
                  <div className="text-xs text-slate-400 font-mono mt-0.5">
                    編號：{displayValue(supplierMaster?.code || supplierMaster?.id)} ｜ 統編：
                    {displayValue(supplierMaster?.tax_id || supplierMaster?.unified_business_no)}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => setShowSupplierDrawer(false)}
                  className="p-2 text-slate-400 hover:text-slate-600 rounded-lg hover:bg-slate-100 text-lg"
                >
                  ✕
                </button>
              </div>

              {loadingSupplier ? (
                <Loading label="讀取供應商主檔資料中…" />
              ) : (
                <div className="space-y-4 text-xs">
                  {/* 基本資料 */}
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">🏢 基本資料</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>公司簡稱：{displayValue(supplierMaster?.short_name)}</div>
                      <div>
                        廠商狀態：
                        {hasValue(supplierMaster?.vendor_status)
                          ? labelVendorStatus(supplierMaster.vendor_status)
                          : '—'}
                      </div>
                      <div className="col-span-2">
                        主要代理／品牌：{displayValue(supplierBrands)}
                      </div>
                      <div className="col-span-2">
                        供應商品類：{displayValue(supplierCategories)}
                      </div>
                      <div>
                        電話：{displayValue(supplierMaster?.phone || supplierMaster?.contact_phone)}
                      </div>
                      <div>
                        Email：
                        {displayValue(supplierMaster?.email || supplierMaster?.company_email)}
                      </div>
                      <div className="col-span-2">地址：{supplierMaster?.address || '—'}</div>
                    </div>
                  </div>

                  {/* 主要聯絡人 */}
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">👥 主要聯絡人</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>姓名：{displayValue(supplierPrimaryContact?.name)}</div>
                      <div>
                        職稱：
                        {displayValue(
                          supplierPrimaryContact?.title || supplierPrimaryContact?.job_title,
                        )}
                      </div>
                      <div>電話：{displayValue(supplierPrimaryContact?.phone)}</div>
                      <div>手機：{displayValue(supplierPrimaryContact?.mobile)}</div>
                      <div className="col-span-2">
                        Email：{displayValue(supplierPrimaryContact?.email)}
                      </div>
                    </div>
                  </div>

                  {/* 交易與付款條件 */}
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">💳 交易與付款條件</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>
                        付款條件：
                        {hasValue(supplierFinancial?.payment_term)
                          ? `${labelVendorPaymentTerm(supplierFinancial.payment_term)}${
                              supplierFinancial.monthly_payment_days
                                ? ` ${supplierFinancial.monthly_payment_days} 天`
                                : ''
                            }`
                          : '—'}
                      </div>
                      <div>
                        付款方式：
                        {hasValue(supplierFinancial?.payment_method)
                          ? labelVendorPaymentMethod(supplierFinancial.payment_method)
                          : '—'}
                      </div>
                      <div>
                        最低訂購金額：
                        <Money value={supplierDelivery?.minimum_order_amount} />
                      </div>
                      <div>
                        最低訂購量：{displayValue(supplierDelivery?.minimum_order_quantity)}
                      </div>
                    </div>
                  </div>

                  {/* 配送條件 */}
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">🚚 配送與物流</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>
                        可配送：
                        {hasValue(supplierDelivery?.delivery_scope)
                          ? labelVendorDeliveryScope(supplierDelivery.delivery_scope)
                          : '—'}
                      </div>
                      <div>
                        訂貨週期：
                        {hasValue(supplierDelivery?.delivery_cycle)
                          ? labelVendorDeliveryCycle(supplierDelivery.delivery_cycle)
                          : '—'}
                      </div>
                      <div>
                        固定配送星期：
                        {formatVendorWeekdays(
                          supplierDelivery?.delivery_weekdays || supplierDelivery?.fixed_weekdays,
                        )}
                      </div>
                      <div>
                        運費規則：
                        {hasValue(supplierDelivery?.freight_rule)
                          ? labelVendorFreightRule(supplierDelivery.freight_rule)
                          : '—'}
                      </div>
                      {hasValue(supplierDelivery?.freight_threshold_amount) && (
                        <div>
                          免運門檻：
                          <Money value={supplierDelivery.freight_threshold_amount} />
                        </div>
                      )}
                      {hasValue(supplierDelivery?.freight_fixed_amount) && (
                        <div>
                          固定運費：
                          <Money value={supplierDelivery.freight_fixed_amount} />
                        </div>
                      )}
                    </div>
                  </div>

                  {/* 退換貨條件 */}
                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">🔄 退換貨機制</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>
                        退貨地點：
                        {hasValue(supplierReturns?.return_scope)
                          ? labelVendorDeliveryScope(supplierReturns.return_scope)
                          : '—'}
                      </div>
                      <div>
                        處理方式：
                        {hasValue(supplierReturns?.handling_method)
                          ? labelVendorReturnHandling(supplierReturns.handling_method)
                          : '—'}
                      </div>
                      <div className="col-span-2">
                        成立條件：
                        {formatVendorReturnConditions(
                          supplierReturns?.conditions || supplierReturns?.return_conditions,
                        )}
                      </div>
                      <div>即期條件：{displayValue(supplierReturns?.near_expiry_condition)}</div>
                      <div>瑕疵條件：{displayValue(supplierReturns?.defect_condition)}</div>
                      <div className="col-span-2">
                        特殊說明：{displayValue(supplierReturns?.condition_description)}
                      </div>
                      <div className="col-span-2">
                        運費負擔：
                        {hasValue(supplierReturns?.freight_responsibility)
                          ? labelVendorReturnFreight(supplierReturns.freight_responsibility)
                          : '—'}
                        {hasValue(supplierReturns?.freight_rule_note)
                          ? `（${supplierReturns.freight_rule_note}）`
                          : ''}
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                    <h4 className="font-bold text-slate-800 text-sm">🧾 發票</h4>
                    <div className="grid grid-cols-2 gap-2 text-slate-600">
                      <div>
                        發票格式：
                        {hasValue(supplierInvoice?.invoice_format)
                          ? labelVendorInvoiceFormat(supplierInvoice.invoice_format)
                          : '—'}
                      </div>
                      <div>
                        寄送方式：
                        {hasValue(supplierInvoice?.invoice_delivery_method)
                          ? labelVendorInvoiceDelivery(supplierInvoice.invoice_delivery_method)
                          : '—'}
                      </div>
                    </div>
                  </div>

                  {supplierMaster?.bank_data_access &&
                    supplierMaster?.bank_accounts?.length > 0 && (
                      <div className="p-4 bg-slate-50 rounded-xl space-y-2">
                        <h4 className="font-bold text-slate-800 text-sm">
                          🏦 銀行資料（依目前角色權限）
                        </h4>
                        {supplierMaster.bank_accounts.map((account: any) => (
                          <div key={account.id} className="grid grid-cols-2 gap-2 text-slate-600">
                            <div>銀行：{displayValue(account.bank_name)}</div>
                            <div>分行：{displayValue(account.branch_name)}</div>
                            <div>戶名：{displayValue(account.account_name)}</div>
                            <div>帳號：{displayValue(account.account_number)}</div>
                          </div>
                        ))}
                      </div>
                    )}

                  {meetingSupplierSnapshot && (
                    <div className="p-4 bg-amber-50 rounded-xl border border-amber-200 space-y-2">
                      <h4 className="font-bold text-amber-900 text-sm">🕘 會議進場時條件快照</h4>
                      <p className="text-slate-600">
                        快照時間：{displayValue(meetingSupplierSnapshot.snapshot_at)}
                        。此區保留當時決策條件；上方為目前正式廠商主檔。
                      </p>
                      <div className="grid gap-2 text-slate-700 sm:grid-cols-3">
                        <div className="rounded-lg bg-white/70 p-2">
                          <span className="block font-bold text-amber-900">付款</span>
                          {hasValue(meetingSupplierFinancial.payment_term)
                            ? labelVendorPaymentTerm(meetingSupplierFinancial.payment_term)
                            : '—'}
                          {hasValue(meetingSupplierFinancial.monthly_payment_days)
                            ? ` ${meetingSupplierFinancial.monthly_payment_days} 天`
                            : ''}
                          {' ｜ '}
                          {hasValue(meetingSupplierFinancial.payment_method)
                            ? labelVendorPaymentMethod(meetingSupplierFinancial.payment_method)
                            : '—'}
                        </div>
                        <div className="rounded-lg bg-white/70 p-2">
                          <span className="block font-bold text-amber-900">配送</span>
                          {hasValue(meetingSupplierDelivery.delivery_scope)
                            ? labelVendorDeliveryScope(meetingSupplierDelivery.delivery_scope)
                            : '—'}
                          {' ｜ '}
                          {hasValue(meetingSupplierDelivery.delivery_cycle)
                            ? labelVendorDeliveryCycle(meetingSupplierDelivery.delivery_cycle)
                            : '—'}
                          {parseList(meetingSupplierDelivery.delivery_weekdays).length > 0
                            ? `（${formatVendorWeekdays(meetingSupplierDelivery.delivery_weekdays)}）`
                            : ''}
                        </div>
                        <div className="rounded-lg bg-white/70 p-2">
                          <span className="block font-bold text-amber-900">退貨／換貨</span>
                          {hasValue(meetingSupplierReturns.handling_method)
                            ? labelVendorReturnHandling(meetingSupplierReturns.handling_method)
                            : '—'}
                          {' ｜ '}
                          {formatVendorReturnConditions(meetingSupplierReturns.conditions)}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="pt-4 border-t border-slate-200 text-right">
              <Button onClick={() => setShowSupplierDrawer(false)}>關閉視窗</Button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 完整 1+N 競品比較 Modal (彈窗) */}
      {/* ======================================================== */}
      {showFullCompareModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4">
          <div className="w-full max-w-6xl bg-white max-h-[90vh] rounded-2xl shadow-2xl flex flex-col">
            <div className="p-4 border-b border-slate-200 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-ink">
                  新品與惠生競品完整比對（1 + {competitors.length} 矩陣）
                </h3>
                <p className="text-xs text-slate-500">
                  本次新品：{propName} ｜ 內部競品共 {competitors.length} 項
                </p>
              </div>
              <button
                type="button"
                onClick={() => setShowFullCompareModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 rounded-lg text-lg"
              >
                ✕
              </button>
            </div>

            <div className="p-4 overflow-y-auto flex-1">
              <div className="overflow-x-auto rounded-xl border border-slate-200">
                <Table>
                  <thead>
                    <tr className="bg-slate-100 text-slate-800 text-xs">
                      <th className="p-3 text-left">比較指標</th>
                      <th className="p-3 bg-teal-50 text-left font-bold text-teal-900 border-l border-r border-teal-200">
                        🌟 本次新品：{propName}
                      </th>
                      {competitors.map((c, i) => (
                        <th
                          key={c.id || i}
                          className={`p-3 text-left ${
                            c.is_primary
                              ? 'bg-amber-50 border-l border-r border-amber-300 font-bold'
                              : ''
                          }`}
                        >
                          <div>{c.is_primary ? '★ 主要競品' : `競品 #${i + 1}`}</div>
                          <div className="font-bold text-ink">{c.product_name}</div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100 text-xs">
                    {fullComparisonRows.map((row, index) => {
                      const showGroup =
                        index === 0 || fullComparisonRows[index - 1]?.group !== row.group;
                      return (
                        <tr key={`${row.group}-${row.label}`}>
                          <td className="p-2.5 font-bold text-slate-600 align-top">
                            {showGroup && (
                              <span className="mb-1 block text-[10px] font-extrabold text-teal-700">
                                {row.group}
                              </span>
                            )}
                            {row.label}
                          </td>
                          <td className="p-2.5 bg-teal-50/30 border-l border-r border-teal-100 whitespace-pre-wrap break-words align-top">
                            {row.proposal}
                          </td>
                          {competitors.map((competitor) => (
                            <td
                              key={competitor.id}
                              className={`p-2.5 whitespace-pre-wrap break-words align-top ${
                                competitor.is_primary ? 'bg-amber-50/20 font-semibold' : ''
                              }`}
                            >
                              {row.competitor(competitor)}
                            </td>
                          ))}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </div>
            </div>

            <div className="p-4 border-t border-slate-200 text-right">
              <Button onClick={() => setShowFullCompareModal(false)}>關閉彈窗</Button>
            </div>
          </div>
        </div>
      )}

      {/* ======================================================== */}
      {/* 底部 Sticky 決策動作列 (含商品策略與舊品去化) */}
      {/* ======================================================== */}
      {selectedProposal && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur border-t border-slate-200 p-4 shadow-2xl">
          <div className="max-w-[1600px] mx-auto space-y-3">
            {/* 上排：決議狀態與商品策略 */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold text-slate-700">會議決議：</span>
                <div className="flex gap-1.5">
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      decision === 'APPROVED'
                        ? 'bg-emerald-600 text-white shadow'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setDecision('APPROVED')}
                  >
                    ✓ 決議上市
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      decision === 'REVISION_REQUIRED'
                        ? 'bg-amber-500 text-white shadow'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setDecision('REVISION_REQUIRED')}
                  >
                    ↺ 要求補件
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      decision === 'PAUSED'
                        ? 'bg-sky-600 text-white shadow'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setDecision('PAUSED')}
                  >
                    ⏸ 暫緩評估
                  </button>
                  <button
                    type="button"
                    className={`px-3 py-1.5 rounded-xl text-xs font-bold transition ${
                      decision === 'NOT_ADOPTED'
                        ? 'bg-rose-600 text-white shadow'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                    onClick={() => setDecision('NOT_ADOPTED')}
                  >
                    ✕ 不採用
                  </button>
                </div>
              </div>

              {/* 若決議上市：商品策略選擇 */}
              {decision === 'APPROVED' && (
                <div className="flex flex-wrap items-center gap-2 text-xs">
                  <span className="font-bold text-teal-800">商品策略：</span>
                  <Select
                    value={productStrategy}
                    onChange={(e) => setProductStrategy(e.target.value as ProductStrategy)}
                    className="text-xs py-1 px-2.5 font-bold"
                  >
                    {LAUNCH_PRODUCT_STRATEGIES.map((strategy) => (
                      <option key={strategy} value={strategy}>
                        {PRODUCT_STRATEGY_LABELS[strategy]}
                      </option>
                    ))}
                  </Select>

                  {/* 若選擇取代：指定被取代商品與舊品處理方式 */}
                  {productStrategy === 'REPLACE' && (
                    <>
                      <span className="text-slate-500 ml-1">被取代商品：</span>
                      <Select
                        value={replacedProductId}
                        onChange={(e) => setReplacedProductId(e.target.value)}
                        className="text-xs py-1 px-2 font-bold"
                      >
                        {competitors.map((c) => (
                          <option key={c.id} value={c.id}>
                            {c.is_primary ? '★ ' : ''}
                            {c.product_name} ({c.product_code || c.itemId})
                          </option>
                        ))}
                      </Select>

                      <span className="text-slate-500 ml-1">舊品去化方式：</span>
                      <Select
                        value={replacedHandling}
                        onChange={(e) =>
                          setReplacedHandling(e.target.value as ReplacedProductHandling)
                        }
                        className="text-xs py-1 px-2"
                      >
                        {Object.entries(REPLACED_PRODUCT_HANDLING_LABELS).map(([k, v]) => (
                          <option key={k} value={k}>
                            {v}
                          </option>
                        ))}
                      </Select>
                      {replacedHandling === 'OTHER' && (
                        <input
                          type="text"
                          placeholder="請填寫其他去化方式"
                          value={replacedHandlingOther}
                          onChange={(event) => setReplacedHandlingOther(event.target.value)}
                          className="w-56 rounded-lg border border-teal-300 px-2.5 py-1 text-xs focus:outline-none"
                        />
                      )}
                    </>
                  )}

                  {/* 若選擇並行：填寫並行原因 */}
                  {productStrategy === 'COEXIST' && (
                    <input
                      type="text"
                      placeholder="請填寫並行原因（如價格帶區隔、不同客群…）"
                      value={coexistenceReason}
                      onChange={(e) => setCoexistenceReason(e.target.value)}
                      className="text-xs px-2.5 py-1 border border-teal-300 rounded-lg w-64 focus:outline-none"
                    />
                  )}
                </div>
              )}
            </div>

            {/* 下排：決策備註與提交按鈕 */}
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex-1 max-w-3xl">
                <input
                  type="text"
                  className="w-full text-xs px-3 py-2 border border-slate-200 rounded-xl focus:outline-none focus:border-teal-500"
                  placeholder={
                    decision === 'APPROVED'
                      ? '上市決策備註（選填，可備註首批配額或上市行銷配合）'
                      : '請填寫決策原因或補件要求說明（必填）…'
                  }
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </div>

              {decision === 'PAUSED' && (
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">預計重評日：</span>
                  <input
                    type="date"
                    min={meetingToday}
                    className="text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg"
                    value={reconsiderationDate}
                    onChange={(e) => setReconsiderationDate(e.target.value)}
                  />
                </div>
              )}

              {decision === 'NOT_ADOPTED' && (
                <div className="flex flex-wrap items-center gap-2">
                  <Select
                    value={rejectionReasonCode}
                    onChange={(event) => setRejectionReasonCode(event.target.value)}
                    className="px-2 py-1 text-xs"
                  >
                    <option value="">請選擇不採用原因</option>
                    {Object.entries(PROACTIVE_REJECTION_REASONS).map(([code, label]) => (
                      <option key={code} value={code}>
                        {label}
                      </option>
                    ))}
                  </Select>
                  <input
                    type="text"
                    value={vendorVisibleReason}
                    onChange={(event) => setVendorVisibleReason(event.target.value)}
                    placeholder="請填寫廠商可見的不採用說明"
                    className="w-72 rounded-lg border border-rose-200 px-2.5 py-1.5 text-xs focus:outline-none focus:border-rose-400"
                  />
                </div>
              )}

              {decision === 'REVISION_REQUIRED' && (
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1.5 text-xs text-slate-600">
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={revisionItems.includes('PRODUCT_BASIC')}
                        onChange={(e) => {
                          if (e.target.checked)
                            setRevisionItems([...revisionItems, 'PRODUCT_BASIC']);
                          else setRevisionItems(revisionItems.filter((i) => i !== 'PRODUCT_BASIC'));
                        }}
                        className="rounded text-amber-600"
                      />
                      <span>基本資料</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={revisionItems.includes('PRICES')}
                        onChange={(e) => {
                          if (e.target.checked) setRevisionItems([...revisionItems, 'PRICES']);
                          else setRevisionItems(revisionItems.filter((i) => i !== 'PRICES'));
                        }}
                        className="rounded text-amber-600"
                      />
                      <span>價格條件</span>
                    </label>
                    <label className="flex items-center gap-1 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={revisionItems.includes('TRADE_TERMS')}
                        onChange={(e) => {
                          if (e.target.checked) setRevisionItems([...revisionItems, 'TRADE_TERMS']);
                          else
                            setRevisionItems(
                              revisionItems.filter((item) => item !== 'TRADE_TERMS'),
                            );
                        }}
                        className="rounded text-amber-600"
                      />
                      <span>交易條件</span>
                    </label>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-slate-500">補件期限：</span>
                    <input
                      type="date"
                      min={meetingToday}
                      className="text-xs px-2.5 py-1.5 border border-slate-200 rounded-lg"
                      value={revisionDueAt}
                      onChange={(e) => setRevisionDueAt(e.target.value)}
                    />
                  </div>
                </div>
              )}

              <Button
                onClick={handleDecide}
                loading={deciding}
                className={`text-xs px-6 py-2.5 font-bold shadow-md ${
                  decision === 'APPROVED'
                    ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                    : decision === 'REVISION_REQUIRED'
                      ? 'bg-amber-600 hover:bg-amber-700 text-white'
                      : 'bg-slate-800 text-white'
                }`}
              >
                確認提交會議決策
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
